import { AuthWrapper } from '@/app/components/AuthWrapper/AuthWrapper'
import { OnboardingType } from '@/app/const'
import { AnketaPage } from '@/app/pages/AnketaPage/AnketaPage'
import { ChatPage } from '@/app/pages/ChatPage/ChatPage'
import { ChatsPage } from '@/app/pages/ChatsPage/ChatsPage'
import { DailyV2Page } from '@/app/pages/DailyV2Page/DailyV2Page'
import { HelpItemPage } from '@/app/pages/HelpPage/HelpItemPage'
import { HelpPage } from '@/app/pages/HelpPage/HelpPage'
import { LandingPageV2 } from '@/app/pages/LandingPage/LandingPage'
import { LocationPage } from '@/app/pages/LocationPage/LocationPage'
import { MatrixArcanaListPage } from '@/app/pages/MatrixArcanaListPage/MatrixArcanaListPage'
import { MatrixArcanaPage } from '@/app/pages/MatrixArcanaPage/MatrixArcanaPage'
import { MatrixCalcPage } from '@/app/pages/MatrixPage/MatrixCalcPage'
import { MoonDayPage } from '@/app/pages/MoonDayPage/MoonDayPage'
import { MoonFullPage } from '@/app/pages/MoonFullPage/MoonFullPage'
import { MoonLocationPage } from '@/app/pages/MoonLocationPage/MoonLocationPage'
import { MoonNewPage } from '@/app/pages/MoonNewPage/MoonNewPage'
import { MoonPage } from '@/app/pages/MoonPage/MoonPage'
import { MoonPhasePage } from '@/app/pages/MoonPhasePage/MoonPhasePage'
import { MoonSignPage } from '@/app/pages/MoonSignPage/MoonSignPage'
import { NatalChartPage } from '@/app/pages/NatalChartPage/NatalChartPage'
import { SettingsPage } from '@/app/pages/SettingsPage/SettingsPage'
import { SubscriptionSuccessPage } from '@/app/pages/SubscriptionSuccessPage/SubscriptionSuccessPage'
import { SynastryPage } from '@/app/pages/SynastryPage/SynastryPage'
import { SynastrySharePage } from '@/app/pages/SynastryPage/SynastryV2/SynastryShare'
import { ToolsPage } from '@/app/pages/ToolsPage/ToolsPage'
import { type ChatStore, ChatStoreContext } from '@/app/state/chat'
import { type DailyStore, DailyStoreContext } from '@/app/state/daily'
import { type MatrixStore, MatrixStoreContext } from '@/app/state/matrix'
import { type NatalStore, NatalStoreContext } from '@/app/state/natal'
import { type SessionStore, SessionStoreContext } from '@/app/state/session'
import { type AppData, AppDataContext, EMPTY_APP_DATA } from '@/app/utils/app-data'
import type { FunctionalComponent } from 'preact'
import { useLayoutEffect } from 'preact/hooks'
import { Route, Router, Switch } from 'wouter-preact'

export const App: FunctionalComponent<{
  url?: string
  appData?: AppData
  sessionStore: SessionStore
  chatStore: ChatStore
  matrixStore: MatrixStore
  natalStore: NatalStore
  dailyStore: DailyStore
}> = props => {
  const initSession = props.sessionStore(s => s.init)
  const resetSession = props.sessionStore(s => s.reset)
  const fetchSession = props.sessionStore(s => s.fetch)

  useLayoutEffect(() => {
    initSession(props.appData?.session)
    window.addEventListener('focus', fetchSession)

    return () => {
      resetSession()
      window.removeEventListener('focus', fetchSession)
    }
  }, [])

  return (
    <SessionStoreContext.Provider value={props.sessionStore}>
      <ChatStoreContext.Provider value={props.chatStore}>
        <MatrixStoreContext.Provider value={props.matrixStore}>
          <NatalStoreContext.Provider value={props.natalStore}>
            <DailyStoreContext.Provider value={props.dailyStore}>
              <AppDataContext.Provider value={props.appData ?? EMPTY_APP_DATA}>
                <Router ssrPath={props.url}>
                  <Switch>
                    <Route path='/'>
                      <LandingPageV2 />
                    </Route>

                    <Route path='/anketa'>
                      <AnketaPage />
                    </Route>

                    <Route path='/help'>
                      <HelpPage />
                    </Route>

                    <Route path='/help/:id'>
                      <HelpItemPage />
                    </Route>

                    <Route path='/start'>
                      <AuthWrapper
                        key='start'
                        type={OnboardingType.Daily}
                      >
                        <DailyV2Page />
                      </AuthWrapper>
                    </Route>

                    <Route path='/login'>
                      <AuthWrapper
                        key='login'
                        type={OnboardingType.Login}
                      />
                    </Route>

                    <Route path='/subscription/success'>
                      <SubscriptionSuccessPage />
                    </Route>

                    <Route path='/settings'>
                      <AuthWrapper
                        key='settings'
                        type={OnboardingType.Base}
                      >
                        <SettingsPage />
                      </AuthWrapper>
                    </Route>

                    <Route path='/settings/location'>
                      <AuthWrapper
                        key='location'
                        type={OnboardingType.Base}
                      >
                        <LocationPage />
                      </AuthWrapper>
                    </Route>

                    <Route path='/chats'>
                      <ChatsPage />
                    </Route>

                    <Route path='/chats/zoe'>
                      <AuthWrapper
                        key='zoe'
                        type={OnboardingType.Astro}
                      >
                        <ChatPage
                          key='zoe'
                          bot='zoe'
                        />
                      </AuthWrapper>
                    </Route>

                    <Route path='/chats/milana'>
                      <AuthWrapper
                        key='milana'
                        type={OnboardingType.Matrix}
                      >
                        <ChatPage
                          key='milana'
                          bot='milana'
                        />
                      </AuthWrapper>
                    </Route>

                    <Route path='/chats/eva'>
                      <AuthWrapper
                        key='eva'
                        type={OnboardingType.Tarot}
                      >
                        <ChatPage
                          key='eva'
                          bot='eva'
                        />
                      </AuthWrapper>
                    </Route>

                    <Route path='/tools'>
                      <ToolsPage />
                    </Route>

                    <Route path='/tools/natal'>
                      <AuthWrapper
                        key='natal'
                        type={OnboardingType.Astro}
                      >
                        <NatalChartPage />
                      </AuthWrapper>
                    </Route>

                    <Route path='/tools/matrix'>
                      <MatrixCalcPage key='matrix' />
                    </Route>

                    <Route path='/tools/synastry' nest>
                      <AuthWrapper
                        key='synastry'
                        type={OnboardingType.Synastry}
                      >
                        <SynastryPage />
                      </AuthWrapper>
                    </Route>

                    <Route path='/syn/:id' nest>
                      <SynastrySharePage />
                    </Route>

                    <Route path='/guide/matrix-arcana'>
                      <MatrixArcanaListPage type='Base' />
                    </Route>

                    <Route path='/guide/matrix-arcana/:id'>
                      <MatrixArcanaPage type='Base' />
                    </Route>

                    <Route path='/guide/matrix-money'>
                      <MatrixArcanaListPage type='Money' />
                    </Route>

                    <Route path='/guide/matrix-money/:id'>
                      <MatrixArcanaPage type='Money' />
                    </Route>

                    {
                      /* <Route path='/topics'>
                      <TopicsPage />
                    </Route> */
                    }

                    <Route path='/moon'>
                      <MoonPage />
                    </Route>

                    <Route path='/moon/sign/:sign'>
                      <MoonSignPage />
                    </Route>

                    <Route path='/moon/day/:num'>
                      <MoonDayPage />
                    </Route>

                    <Route path='/moon/full-moon/:sign'>
                      <MoonFullPage />
                    </Route>

                    <Route path='/moon/new-moon/:sign'>
                      <MoonNewPage />
                    </Route>

                    <Route path='/moon/phase/:phase'>
                      <MoonPhasePage />
                    </Route>

                    <Route path='/moon/location'>
                      <MoonLocationPage />
                    </Route>
                  </Switch>
                </Router>

                {/* <CookieBanner /> */}
              </AppDataContext.Provider>
            </DailyStoreContext.Provider>
          </NatalStoreContext.Provider>
        </MatrixStoreContext.Provider>
      </ChatStoreContext.Provider>
    </SessionStoreContext.Provider>
  )
}
